import { useMutation, useQuery } from '@tanstack/react-query';

import {
  fetchVisitSummaryAvailability,
  fetchVisitSummaryPdf,
} from '@/services/main/rezept';
import { useClinicContext } from './useClinicContext';

export interface VisitSummaryParams {
  search_yearmonth?: string;
  search_visitmonth?: string;
}

/**
 * 令和8年法改正 No 7 — 訪問施術総括表.
 *
 * Whether the button appears at all: the month must have billed 訪問施術料4 or 5 at some
 * 拠点. A practice with nothing to file sees NO button rather than a disabled one
 * (客先確定 2026-08-17, feedback No.72).
 */
export const useVisitSummaryAvailability = (params: VisitSummaryParams, enabled = true) => {
  const { tenant, clinicNumber } = useClinicContext();

  return useQuery({
    queryKey: ['visit-summary-availability', tenant, clinicNumber, params],
    queryFn: () => fetchVisitSummaryAvailability(tenant ?? '', clinicNumber ?? '', params),
    enabled: enabled && Boolean(tenant && clinicNumber && params.search_yearmonth),
  });
};

/**
 * Fetches and opens the report.
 *
 * The server refuses with 422 until every rezept of the submission month is 確定 and
 * carries the message to show; the blob response type means that body arrives as a Blob,
 * so it has to be read back as text before the message can be surfaced.
 */
const OBJECT_URL_TTL_MS = 60_000;

export const useVisitSummaryPdf = () => {
  const { tenant, clinicNumber } = useClinicContext();

  return useMutation({
    mutationFn: (params: VisitSummaryParams) =>
      fetchVisitSummaryPdf(tenant ?? '', clinicNumber ?? '', params),
    onSuccess: blob => {
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener');
      window.setTimeout(() => URL.revokeObjectURL(url), OBJECT_URL_TTL_MS);
    },
  });
};

/**
 * Pull the server's message out of a failed blob request.
 *
 * With responseType 'blob' an error body is a Blob too, so the JSON inside it is not on
 * the error object where callers would normally look for it.
 */
export const readVisitSummaryError = async (error: unknown, fallback: string): Promise<string> => {
  const data = (error as { response?: { data?: unknown } })?.response?.data;

  if (data instanceof Blob) {
    try {
      const parsed = JSON.parse(await data.text()) as { message?: string };
      if (parsed?.message) return parsed.message;
    } catch {
      // Not JSON — fall through to the caller's default.
    }
  }

  const message = (data as { message?: string } | undefined)?.message;
  return message ?? fallback;
};
